import { graphql } from '@/gql';

export const FetchTraineeAssessmentResultPrograms = graphql(`
    query FetchTraineeAssessmentResultPrograms($filter: ProgramFilteringRule) {
        programs(filter: $filter) {
            count
            items {
                ...TraineeAssessmentResultProgramItemData
            }
        }
    }
`);

const TraineeAssessmentResultProgramItemData = graphql(`
    fragment TraineeAssessmentResultProgramItemData on Program {
        id
        name
        description

        courseInstanceId

        assessmentRuns {
            ...TraineeAssessmentResultAssessmentRunData
        }

        courseInstance {
            executionSequence {
                moduleInstanceId
                order
                moduleInstance {
                    ...AssessmentResultModuleInstanceData
                }
            }
        }
    }
`);

const TraineeAssessmentResultAssessmentRunData = graphql(`
    fragment TraineeAssessmentResultAssessmentRunData on AssessmentRun {
        id
        name
        status
        subGroups {
            id
            name
        }
        group {
            ...TraineeAssessmentResultGroup
        }
    }
`);

const TraineeAssessmentResultGroup = graphql(`
    fragment TraineeAssessmentResultGroup on Group {
        id
        name
        allMembers {
            ...TraineeAssessmentResultGroupMember
        }
    }
`);

const TraineeAssessmentResultGroupMember = graphql(`
    fragment TraineeAssessmentResultGroupMember on User {
        ... on TenantUser {
            ...TenantUserReportData
        }
    }
`);

const AssessmentResultModuleInstanceData = graphql(`
    fragment AssessmentResultModuleInstanceData on ModuleInstance {
        id
        name
        description
        moduleDefinitionIdentifier
    }
`);

const TenantUserReportData = graphql(`
    fragment TenantUserReportData on TenantUser {
        id
        name
        email
        firstName
        lastName
        gender
        dateOfBirth
        height
        allGroupIds
        allSubGroupIds
    }
`);

export const FetchTraineePerformanceResults = graphql(`
    query FetchTraineePerformanceResults(
        $filter: PerformanceResultFilterRule
        $pagination: Pagination
        $sort: PerformanceResultSortingRule
    ) {
        performanceResults(filter: $filter, pagination: $pagination, sort: $sort) {
            count
            items {
                ...PerformanceResultListItemData
            }
        }
    }
`);

export const AssessmentResults = graphql(`
    query AssessmentResults($filter: AssessmentResultFilterRule) {
        assessmentResults(filter: $filter) {
            count
            items {
                ...AssessmentResultItemData
            }
            teamResilienceItems {
                ...AssessmentResultItemData
            }
        }
    }
`);

export const IncompleteModules = graphql(`
    query IncompleteModules($programId: ObjectID!, $userId: ObjectID!, $assessmentRunId: ObjectID!) {
        incompleteModules(programId: $programId, userId: $userId, assessmentRunId: $assessmentRunId) {
            ...AssessmentResultItemData
        }
    }
`);

const AssessmentResultItemData = graphql(`
    fragment AssessmentResultItemData on AssessmentResult {
        id
        user {
            ... on TenantUser {
                ...TenantUserReportData
            }
        }
        assessmentRun {
            program {
                id
                name
            }
        }
        moduleInstance {
            id
            name
            moduleDefinitionIdentifier
            description
            currentConfiguration {
                __typename
                ... on OrganisationalAiReadinessModuleInstanceConfiguration {
                    levels {
                        name
                        categoryDescriptions {
                            category
                            description
                        }
                        badgeColor
                    }
                }
            }
        }
        completedAt
        result {
            ... on ML360Result {
                ...ML360ReportData
            }
            ... on SartResult {
                ...SartReportData
            }
            ... on SelfDeterminationResult {
                ...SelfDeterminationReportData
            }
            ... on IndividualMotivationResult {
                ...IndividualMotivationReportData
            }
            ... on SpatialPlanningResult {
                ...SpatialPlanningReportData
            }
            ... on TeamResilienceResult {
                ...TeamResilienceReportData
            }
            ... on VisualReactionResult {
                ...VisualReactionReportData
            }
            ... on StrengthResult {
                ...StrengthAssessmentResultData
            }
            ... on CardioRespResult {
                ...CardioRespAssessmentResultData
            }
            ... on DigitSpanResult {
                ...TraineeDigitSpanAssessmentResultData
            }
            ... on TrainersNotesResult {
                ...TrainersNotesAssessmentResultData
            }
            ... on InbodyCompositionResult {
                ...AssessmentResultInbodyCompositionResultData
            }
            ... on NBackResult {
                ...NBackReportData
            }
            ... on GrowthMindsetResult {
                ...GrowthMindsetReportData
            }
            ... on BurnoutInventoryResult {
                ...BurnoutInventoryAssessmentResultData
            }
            ... on OrganisationalAiReadinessResult {
                ...OrganisationalAiReadinessReportData
            }
            ... on AitResult {
                ...AitReportData
            }
            ... on GoNoGoResult {
                ...GoNoGoReportData
            }
            ... on ConcentrationGridResult {
                ...ConcentrationGridAssessmentResultData
            }
        }
    }
`);

const NBackReportData = graphql(`
    fragment NBackReportData on NBackResult {
        accuracy
        dPrime
        meanHitRtMs
    }
`);

const TrainersNotesAssessmentResultData = graphql(`
    fragment TrainersNotesAssessmentResultData on TrainersNotesResult {
        trainersNotes
        status
    }
`);

const TraineeDigitSpanAssessmentResultData = graphql(`
    fragment TraineeDigitSpanAssessmentResultData on DigitSpanResult {
        compositeScore
        variantSummaries {
            variant
            spanScore
            totalCorrectTrials
            trialsAttempted
            completionStatus
        }
    }
`);

const AitReportData = graphql(`
    fragment AitReportData on AitResult {
        compositeScore
        normalisedAccuracy
        normalisedFlexibility
        normalisedSpeed
        levelDetail {
            level
            label
            description
            minScore
        }
    }
`);

const SartReportData = graphql(`
    fragment SartReportData on SartResult {
        correctHits
        incorrectPresses
        averageResponseTimeMs
    }
`);

const SpatialPlanningReportData = graphql(`
    fragment SpatialPlanningReportData on SpatialPlanningResult {
        accuracy
        averageResponseTimeMs
    }
`);

const VisualReactionReportData = graphql(`
    fragment VisualReactionReportData on VisualReactionResult {
        accuracy
        averageResponseTimeMs
    }
`);

const GoNoGoReportData = graphql(`
    fragment GoNoGoReportData on GoNoGoResult {
        gngScore
        levelLabel
        levelDescription
        levelBadgeColor
        inhibitoryControlScore
        discriminabilityScore
        sustainedAttentionScore
        pressureResilienceScore
    }
`);

const ML360ReportData = graphql(`
    fragment ML360ReportData on ML360Result {
        categoryAverages {
            category
            averageScore
            maxScore
        }
    }
`);

const SelfDeterminationReportData = graphql(`
    fragment SelfDeterminationReportData on SelfDeterminationResult {
        surveys {
            category
        }
        categoryAverages {
            category
            maximumPossibleScore
            averageScore
        }
    }
`);

const IndividualMotivationReportData = graphql(`
    fragment IndividualMotivationReportData on IndividualMotivationResult {
        categoryBreakdowns {
            category
            subcategoryAverages {
                subcategory
                maximumPossibleScore
                averageScore
            }
        }
    }
`);

const TeamResilienceReportData = graphql(`
    fragment TeamResilienceReportData on TeamResilienceResult {
        surveys {
            category
            subcategory
        }
        subcategoryAverages {
            category
            subcategory
            maximumPossibleScore
            averageScore
        }
    }
`);

const GrowthMindsetReportData = graphql(`
    fragment GrowthMindsetReportData on GrowthMindsetResult {
        surveys {
            category
        }
        categoryAverages {
            category
            maximumPossibleScore
            averageScore
            label
            badgeColor
        }
    }
`);

const OrganisationalAiReadinessReportData = graphql(`
    fragment OrganisationalAiReadinessReportData on OrganisationalAiReadinessResult {
        categoryAverages {
            category
            maximumPossibleScore
            averageScore
            label
            description
            badgeColor
        }
        overallScore {
            score
            maximumPossibleScore
            label
        }
    }
`);

export const FetchUserDocument = graphql(`
    query FetchUserDocument($userId: ObjectID!) {
        user(id: $userId) {
            ... on TenantUser {
                ...TenantUserReportData
            }
        }
    }
`);
